import Markdown from 'markdown-to-jsx';
import { IRequirements, ICourseCountry } from '../../../models/course/ICourseData';
import styles from './course-requirements.module.scss';
import Link from 'next/link';
import { DropdownSearch, IOption } from '@into/react-components';
import { IRegionalQualifications } from '../../../models/course/IRegionalQualifications';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ApplicationType } from '../../../constants/application-type';

export type Props = {
  entryRequirements: IRequirements;
  showDisclaimer: boolean;
  iCasNote?: string;
  brandSlug?: string;
  displayiCASNote?: boolean;
  courseLevel?: string;
  classSize?: number;
  iCasUndergraduateExists?: boolean;
  iCasGraduateExists?: boolean;
  regionalQualifications?: IRegionalQualifications[];
  countries?: ICourseCountry[];
  instanceID: string;
  onRequirementsChange?: (requirements: IRequirements) => void;
  applicationType: string;
  currentLocale: string;
};

export default function CourseRequirements({
  entryRequirements,
  showDisclaimer,
  brandSlug,
  courseLevel,
  displayiCASNote,
  iCasNote,
  classSize,
  iCasUndergraduateExists,
  iCasGraduateExists,
  regionalQualifications,
  countries,
  instanceID,
  onRequirementsChange,
  applicationType,
  currentLocale,
}: Props) {
  // only show the iCAS note for the pathway programs
  // of which the brand associated with ICAS entry route
  // fetch iCAS note from associated brandOptions

  let translate = useTranslations();

  let iCasPath: string = null;
  let iCasPageURL: string = null;
  const iCasCourseLevel: string = courseLevel === 'Graduate' ? `postgraduate` : `undergraduate`;
  if (
    (courseLevel === 'Graduate' && iCasGraduateExists) ||
    (courseLevel === 'Undergraduate' && iCasUndergraduateExists)
  ) {
    iCasPath = `/summary/icas-${iCasCourseLevel}`;
    iCasPageURL =
      applicationType === ApplicationType.UniBrandedB2c
        ? `/${currentLocale}${iCasPath}`
        : `/${currentLocale}/universities/${brandSlug}${iCasPath}`;
  }

  // TODO: make constants\study-level.ts an enum (needs refactoring)
  // Add localization for the enum (as stydyLevel is used now)
  // Use this enum here
  const isALevel: boolean = courseLevel === 'ALevel';

  const countryList = useMemo(() => {
    const result = [];

    countries?.forEach((c) => {
      result.push({
        text: c.countryName,
        value: c.salesforceId,
      });
    });

    if (regionalQualifications?.find((r) => !r.countryId && r.type === 'Academic')) {
      result.push({
        text: 'International',
        value: '',
      });
    }

    return result;
  }, [countries, regionalQualifications]);

  const handleChange = useCallback(
    (option: IOption | null): void => {
      const country = option.value;

      if (option.value == null) {
        return;
      }

      const newEntryRequirements = { ...entryRequirements };
      newEntryRequirements.academic = regionalQualifications
        ?.filter(
          (e) =>
            e.countryId?.substring(0, e.countryId?.length - 3) === country && e.type === 'Academic',
        )
        ?.map((e) => {
          return e?.copy;
        })
        .join('|');

      if (!newEntryRequirements.academic) {
        newEntryRequirements.academic = regionalQualifications
          ?.filter((e) => e.type === 'Academic' && !e.countryId)
          ?.map((e) => {
            return e?.copy;
          })
          .join('|');
      }
      newEntryRequirements.language = regionalQualifications
        ?.filter(
          (e) =>
            (e.countryId?.substring(0, e.countryId?.length - 3) === country || !e.countryId) &&
            e.type === 'English - Course',
        )
        ?.map((e) => {
          return e?.copy;
        })
        .join('|');

      if (!newEntryRequirements.language) {
        newEntryRequirements.language = entryRequirements.language;
      }

      if (onRequirementsChange != null) {
        onRequirementsChange(newEntryRequirements);
      }
    },
    [entryRequirements, onRequirementsChange, regionalQualifications],
  );

  return (
    <div key={'dev' + instanceID} className={styles['entry-requirements']}>
      {countryList.length > 0 ? (
        <div className={styles[`dropdownSearch`]}>
          <DropdownSearch
            options={countryList.sort((a, b) => (a.text > b.text ? 1 : -1))}
            onChange={handleChange}
            id={'ddlCountry-' + instanceID}
            label='Please select the country or region to view course details personalised for you.'
            placeholder='Country or Region'
            searchAriaLabel='Entry requirement country or region'
            searchPlaceholder='Search Country or Region'
            clearSearchButtonAriaLabel='Clear Search'
            noOptionsFoundText='No records found'
          ></DropdownSearch>
        </div>
      ) : null}
      {showDisclaimer ? (
        <p>
          Entry requirements are subject to change due to the program selection and the academic
          year.{' '}
        </p>
      ) : null}
      {entryRequirements.minimumAge ? (
        <div>
          <h5>Age Requirement</h5>
          <p>{entryRequirements.minimumAge}</p>
          {entryRequirements.minimumAgeFootnote ? (
            <>
              <Markdown>{entryRequirements.minimumAgeFootnote}</Markdown>
            </>
          ) : null}
        </div>
      ) : null}

      {entryRequirements.academic ? (
        <div>
          <h5>Academic Requirements</h5>
          <>
            {entryRequirements.academic.split('|').length > 1 ? (
              <ul className={styles['ulRequirements']}>
                {entryRequirements.academic.split('|').map((x) => {
                  return (
                    <li key={x.length}>
                      <Markdown>{x}</Markdown>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <Markdown>{entryRequirements.academic}</Markdown>
            )}
          </>
        </div>
      ) : null}
      {entryRequirements.language ? (
        <div>
          <h5>English Language Requirements</h5>
          <>
            {entryRequirements.language.split('|').length > 1 ? (
              <ul className={styles['ulRequirements']}>
                {entryRequirements.language.split('|').map((x) => {
                  return (
                    <li key={x.length}>
                      <Markdown>{x}</Markdown>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <Markdown>{entryRequirements.language}</Markdown>
            )}
          </>
        </div>
      ) : null}
      {classSize ? (
        <div>
          <h5>Class size</h5>
          <p>
            <span>{classSize}</span>
          </p>
        </div>
      ) : null}
      {displayiCASNote && iCasNote && iCasPageURL ? (
        <div>
          <h5>iCAS</h5>
          <p>
            <Markdown>{iCasNote}</Markdown>
          </p>
          <p>
            Apply for one of our <Link href={iCasPageURL}>iCAS programs</Link>
          </p>
        </div>
      ) : null}
      {isALevel ? (
        <p>
          <strong>{translate(`Courses.entryRequirements.note`)}</strong>{' '}
          {translate(`Courses.entryRequirements.noteALevel`)}
        </p>
      ) : (
        <p>
          <strong>{translate(`Courses.entryRequirements.note`)}</strong>{' '}
          {translate(`Courses.entryRequirements.noteNonALevel`)}
        </p>
      )}
    </div>
  );
}
